"use client";

import { SearchIcon, X } from "lucide-react";
import * as React from "react";
import { useCallback } from "react";
import { UseFormReturn } from "react-hook-form";

import ClearFiltersContainer, {
  calculateActiveFilters,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/clear-filters.container";
import {
  FILTER_KEYS,
  FilterValues,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import SavedSearchesAndPreferences from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/saved-searches-and-preferences";
import SearchFiltersView from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/search-filters.view";
import useActiveFilterCount from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-active-filter-count";
import useFiltersForm from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-filters-form";
import useResetFilters from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-reset-filters";
import useUrlState from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-url-state";
import { CountBadge } from "@/components/shared/count-badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import useStateConfig from "@/hooks/use-state-config";

export function SearchFiltersDrawerView({
  form,
  resetFilters,
  activeFilterCount,
  onOpenChange,
  onApplyFilters,
  appliedFilterCount,
  savedSearchesAndPreferences,
}: {
  form: UseFormReturn<FilterValues>;
  resetFilters: () => void;
  activeFilterCount: number;
  onOpenChange: (open: boolean) => void;
  onApplyFilters: () => void;
  appliedFilterCount: number;
  savedSearchesAndPreferences: React.ReactNode;
}) {
  return (
    <Drawer onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <Button
          variant="outline"
          className="relative h-7 text-xs px-2.5 border-line-strong bg-card hover:bg-surface-subtle text-ink-emphasis"
        >
          Filters
          {appliedFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 lg:hidden">
              <CountBadge count={appliedFilterCount} className="ml-0" />
            </span>
          )}
        </Button>
      </DrawerTrigger>
      <DrawerContent
        showCloseButton={false}
        className="h-[96vh] [&>div.bg-muted]:hidden"
      >
        <DrawerHeader className="p-0 text-left border-b border-border">
          <div className="flex items-center justify-between py-3 px-4">
            <div className="w-8 shrink-0" />
            <DrawerTitle className="flex-1 text-center text-base font-semibold m-0 p-0 text-foreground">
              Filters
            </DrawerTitle>
            <DrawerClose asChild>
              <Button
                variant="outline"
                size="icon"
                className="rounded-full shrink-0 h-8 w-8 bg-surface-subtle border-border hover:bg-surface-strong"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </DrawerClose>
          </div>
          <DrawerDescription className="sr-only">
            Search Filters
          </DrawerDescription>
        </DrawerHeader>
        <div className="overflow-auto px-4 py-4 flex-1">
          {savedSearchesAndPreferences}
          <SearchFiltersView form={form} />
        </div>
        <div className="border-t border-border p-4 bg-card flex items-center justify-between sticky bottom-0 w-full mt-auto">
          <ClearFiltersContainer
            resetFilters={resetFilters}
            activeFilterCount={activeFilterCount}
          />
          <DrawerClose asChild>
            <Button
              className="font-semibold px-10 gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => {
                onApplyFilters();
                onOpenChange(false);
              }}
            >
              <SearchIcon className="h-4 w-4" />
              Search
            </Button>
          </DrawerClose>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default function SearchFiltersDrawerContainer({
  ViewComponent = SearchFiltersDrawerView,
}: {
  ViewComponent?: typeof SearchFiltersDrawerView;
}) {
  const { onUrlValuesChange, urlValues } = useUrlState();

  const { form, filterDefaults } = useFiltersForm({
    overrideValues: urlValues,
  });

  const activeFilterCount = useActiveFilterCount(
    form,
    FILTER_KEYS,
    filterDefaults,
  );

  const resetFilters = useResetFilters(form, filterDefaults);

  const { supportsSavedSearches } = useStateConfig();

  const hasSavedSearchesAndPreferences = supportsSavedSearches;

  const appliedFilterCount = React.useMemo(() => {
    const appliedValues = { ...filterDefaults, ...urlValues };
    return calculateActiveFilters(FILTER_KEYS, filterDefaults, appliedValues);
  }, [filterDefaults, urlValues]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      // Reset the form to the default values merged with URL values when the drawer opens
      // to discard unapplied edits from the previous session, completely flushing RHF sparse state.
      if (open) {
        form.reset({ ...filterDefaults, ...urlValues });
      }
    },
    [form, filterDefaults, urlValues],
  );

  const onApplyFilters = useCallback(() => {
    onUrlValuesChange(form.getValues());
  }, [form, onUrlValuesChange]);

  const savedSearchesAndPreferences = hasSavedSearchesAndPreferences ? (
    <SavedSearchesAndPreferences form={form} filterDefaults={filterDefaults} />
  ) : null;

  return (
    <ViewComponent
      onOpenChange={onOpenChange}
      resetFilters={resetFilters}
      activeFilterCount={activeFilterCount}
      form={form}
      onApplyFilters={onApplyFilters}
      appliedFilterCount={appliedFilterCount}
      savedSearchesAndPreferences={savedSearchesAndPreferences}
    />
  );
}
